/** Authored convex coasts and paths solved once at world/order construction.
 * The runtime evaluator only reads cubic coefficients and lengths. No alpha,
 * obstacle scan, clock, integration or progression rule belongs here.
 */
import type { PlaceSeed } from './seed';
import type { Degrees, Vec2 } from './units';
import { bearingDeg, clamp01, distance, lerpVec2, vec2 } from './units';

export type Coast = readonly Vec2[];
export interface CoastSegment {
  readonly start: Vec2;
  readonly control1: Vec2;
  readonly control2: Vec2;
  readonly end: Vec2;
  /** Fixed-subdivision arc table, built with the path. */
  readonly lengths: readonly number[];
  readonly length: number;
}
export interface CoastPath {
  readonly segments: readonly CoastSegment[];
  readonly cumulative: readonly number[];
  readonly length: number;
  readonly start: Vec2;
  readonly end: Vec2;
}
const EPS = 1e-9;
const ROUTING_MARGIN = 0.75;
const CORNER_TRIM = 0.2;
const ARC_STEPS = 24;
const cross = (a: Vec2, b: Vec2, p: Vec2): number =>
  (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);

export function authoredCoastsFor(places: readonly PlaceSeed[]): Coast[] {
  return places.flatMap(p => p.extent?.coastLeagues === undefined ? [] :
    [p.extent.coastLeagues.map(v => vec2(p.position.x + v.x, p.position.y + v.y))]);
}

/** Counter-clockwise convex half-planes. Boundary is conservatively occupied. */
export function coastContains(coast: Coast, point: Vec2): boolean {
  return coast.length >= 3 && coast.every((a, i) => cross(a, coast[(i + 1) % coast.length]!, point) >= -EPS);
}

/** A legacy start can become inland when artwork gains a coast. Move it to the
 * nearest buffered water boundary once; keep the player's destination and ETA. */
export function nearestCoastWater(point: Vec2, coasts: readonly Coast[]): Vec2 {
  if (coasts.every(c => !coastContains(c, point))) return point;
  const candidates: Vec2[] = [];
  for (const coast of coasts) {
    candidates.push(...expandCoast(coast, ROUTING_MARGIN));
    for (let i = 0; i < coast.length; i++) {
      const a = coast[i]!, b = coast[(i + 1) % coast.length]!;
      const dx = b.x - a.x, dy = b.y - a.y, length = Math.hypot(dx, dy);
      if (length <= EPS) continue;
      const t = clamp01(((point.x - a.x) * dx + (point.y - a.y) * dy) / (length * length));
      candidates.push(vec2(a.x + t * dx + dy / length * ROUTING_MARGIN,
        a.y + t * dy - dx / length * ROUTING_MARGIN));
    }
  }
  const safe = candidates.filter(p => coasts.every(c => !coastContains(c, p)));
  safe.sort((a, b) => distance(point, a) - distance(point, b));
  if (safe[0] === undefined) throw new Error('Authored coast has no safe recovery boundary');
  return safe[0];
}

/** Used only during planning/tests. Tangency to the already buffered coast is safe. */
export function segmentCrossesCoast(a: Vec2, b: Vec2, coast: Coast): boolean {
  let lo = 0, hi = 1;
  for (let i = 0; i < coast.length; i++) {
    const p = coast[i]!, q = coast[(i + 1) % coast.length]!;
    const c0 = cross(p, q, a), c1 = cross(p, q, b), slope = c1 - c0;
    if (Math.abs(slope) <= EPS) { if (c0 <= EPS) return false; }
    else if (slope > 0) lo = Math.max(lo, (EPS - c0) / slope);
    else hi = Math.min(hi, (EPS - c0) / slope);
    if (lo >= hi) return false;
  }
  return hi > 0 && lo < 1 && lo < hi;
}

/** Move every supporting half-plane outwards, preserving convexity. */
function expandCoast(coast: Coast, amount: number): Vec2[] {
  return coast.map((p, i) => {
    const a = coast[(i + coast.length - 1) % coast.length]!, b = coast[(i + 1) % coast.length]!;
    const ax = p.x - a.x, ay = p.y - a.y, bx = b.x - p.x, by = b.y - p.y;
    const al = Math.hypot(ax, ay), bl = Math.hypot(bx, by);
    const n1 = vec2(ay / al, -ax / al), n2 = vec2(by / bl, -bx / bl);
    const dot = n1.x * n2.x + n1.y * n2.y;
    return vec2(p.x + amount * (n1.x + n2.x) / (1 + dot), p.y + amount * (n1.y + n2.y) / (1 + dot));
  });
}

/** Deterministic visibility graph; bounded by the authored vertex count. */
function waypoints(from: Vec2, to: Vec2, coasts: readonly Coast[]): Vec2[] {
  const clear = (a: Vec2, b: Vec2): boolean => coasts.every(c => !segmentCrossesCoast(a, b, c));
  if (clear(from, to)) return [from, to];
  const nodes = [from, to, ...coasts.flatMap(c => expandCoast(c, ROUTING_MARGIN))];
  const costs = nodes.map(() => Infinity), previous = nodes.map(() => -1), done = nodes.map(() => false);
  costs[0] = 0;
  for (let count = 0; count < nodes.length; count++) {
    let u = -1;
    for (let i = 0; i < nodes.length; i++) if (!done[i] && (u < 0 || costs[i]! < costs[u]!)) u = i;
    if (u < 0 || !Number.isFinite(costs[u]!)) break;
    if (u === 1) break;
    done[u] = true;
    for (let v = 0; v < nodes.length; v++) {
      if (done[v] || v === u || !clear(nodes[u]!, nodes[v]!)) continue;
      const candidate = costs[u]! + distance(nodes[u]!, nodes[v]!);
      if (candidate < costs[v]!) { costs[v] = candidate; previous[v] = u; }
    }
  }
  if (!Number.isFinite(costs[1]!)) throw new Error('Authored coast has no safe route between these endpoints');
  const path = [to];
  for (let v = previous[1]!; v >= 0; v = previous[v]!) path.push(nodes[v]!);
  return path.reverse();
}

function cubic(start: Vec2, c1: Vec2, c2: Vec2, end: Vec2, t: number): Vec2 {
  const v = 1 - t;
  return vec2(v*v*v*start.x + 3*v*v*t*c1.x + 3*v*t*t*c2.x + t*t*t*end.x,
    v*v*v*start.y + 3*v*v*t*c1.y + 3*v*t*t*c2.y + t*t*t*end.y);
}
function makeSegment(start: Vec2, control1: Vec2, control2: Vec2, end: Vec2): CoastSegment {
  const lengths = [0];
  let previous = start, length = 0;
  for (let i = 1; i <= ARC_STEPS; i++) {
    const p = cubic(start, control1, control2, end, i / ARC_STEPS);
    length += distance(previous, p); lengths.push(length); previous = p;
  }
  return { start, control1, control2, end, lengths, length };
}
function straight(a: Vec2, b: Vec2): CoastSegment {
  return makeSegment(a, lerpVec2(a, b, 1 / 3), lerpVec2(a, b, 2 / 3), b);
}

/** A precomputed safe smooth path. Optional join bearings preserve natural-track tangency. */
export function planCoastPath(from: Vec2, to: Vec2, coasts: readonly Coast[], startHeading?: Degrees, endHeading?: Degrees): CoastPath {
  const lead = (point: Vec2, heading: number | undefined, sign: number): Vec2 => {
    if (heading === undefined) return point;
    const r = heading * Math.PI / 180;
    let reach = Math.min(3, distance(from, to) / 6);
    for (let i = 0; i < 16; i++) {
      const q = vec2(point.x + sign * Math.sin(r) * reach, point.y + sign * Math.cos(r) * reach);
      if (coasts.every(c => !segmentCrossesCoast(point, q, c))) return q;
      reach /= 2;
    }
    return point;
  };
  const a = lead(from, startHeading, 1), b = lead(to, endHeading, -1);
  const middle = waypoints(a, b, coasts);
  const points = [from, ...middle, to].filter((p, i, all) => i === 0 || distance(p, all[i - 1]!) > EPS);
  const segments: CoastSegment[] = [];
  let cursor = points[0]!;
  for (let i = 1; i + 1 < points.length; i++) {
    const prev = points[i - 1]!, p = points[i]!, next = points[i + 1]!;
    const trim = Math.min(CORNER_TRIM, distance(prev, p) / 3, distance(p, next) / 3);
    const before = lerpVec2(p, prev, trim / distance(prev, p));
    const after = lerpVec2(p, next, trim / distance(p, next));
    segments.push(straight(cursor, before));
    const turn = makeSegment(before, lerpVec2(before, p, 2 / 3), lerpVec2(after, p, 2 / 3), after);
    // The graph vertices have an extra routing margin. Endpoint tangent leads may
    // be closer: retain that corner unrounded if its conservative chord is blocked.
    if (coasts.some(c => segmentCrossesCoast(before, after, c))) {
      segments.push(straight(before, p), straight(p, after));
    } else segments.push(turn);
    cursor = after;
  }
  segments.push(straight(cursor, to));
  const cumulative: number[] = [];
  let length = 0;
  for (const segment of segments) { length += segment.length; cumulative.push(length); }
  return { segments, cumulative, length, start: from, end: to };
}

function segmentAt(path: CoastPath, u: number): { segment: CoastSegment; t: number } {
  const d = clamp01(u) * path.length;
  let i = 0;
  while (i + 1 < path.segments.length && path.cumulative[i]! < d) i++;
  const segment = path.segments[i]!;
  const local = d - (i === 0 ? 0 : path.cumulative[i - 1]!);
  let j = 1;
  while (j < ARC_STEPS && segment.lengths[j]! < local) j++;
  const lo = segment.lengths[j - 1]!, hi = segment.lengths[j]!;
  const t = ((j - 1) + (hi > lo ? clamp01((local - lo) / (hi - lo)) : 0)) / ARC_STEPS;
  return { segment, t };
}
export function coastPathPoint(path: CoastPath, u: number): Vec2 {
  if (u <= 0) return path.start;
  if (u >= 1) return path.end;
  const { segment: s, t } = segmentAt(path, u);
  return cubic(s.start, s.control1, s.control2, s.end, t);
}
export function coastPathHeading(path: CoastPath, u: number): Degrees {
  const { segment: s, t } = segmentAt(path, u), v = 1 - t;
  const dx = 3*v*v*(s.control1.x-s.start.x)+6*v*t*(s.control2.x-s.control1.x)+3*t*t*(s.end.x-s.control2.x);
  const dy = 3*v*v*(s.control1.y-s.start.y)+6*v*t*(s.control2.y-s.control1.y)+3*t*t*(s.end.y-s.control2.y);
  return bearingDeg(vec2(0, 0), vec2(dx, dy));
}
